import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FaUsers, 
  FaBullseye, 
  FaEye, 
  FaHeart, 
  FaHandshake,
  FaRocket,
  FaShieldAlt,
  FaAward,
  FaChartLine,
  FaGlobe,
  FaHandHoldingHeart,
  FaUserTie,
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaTrophy,
  FaGem,
  FaCrown,
  FaTree,
  FaInfinity,
  FaCoins,
  FaWallet,
  FaGift,
  FaCar,
  FaUsers as FaUsersGroup,
  FaBuilding,
  FaMobile,
  FaDesktop,
  FaHeadset
} from "react-icons/fa";

// Color palette
const colors = {
  brightBlue: '#00A9EC',
  deepOrange: '#E06207',
  nearBlack: '#0E1306',
  green: '#018F2C',
  darkBrown: '#602005',
  burntOrange: '#A65007',
  lightOrange: '#EF9222',
  darkGreen: '#056619',
  reddishBrown: '#9C2E04',
  darkBlue: '#0067A1',
  skyBlue: '#0DC8F8',
  peachOrange: '#FDA653',
  navyBlue: '#023057',
  oliveBrown: '#604B0C',
  tealBlue: '#024D6E',
  pinkMagenta: '#A70756',
  goldenYellow: '#F3D71B',
  freshGreen: '#12B14A',
  lightGreen: '#55CB71',
  lightCyan: '#56E0F9'
};

function About() {
  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="bg-white">
      {/* ===== HERO SECTION ===== */}
      <section 
        className="pt-32 pb-20 text-white relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.navyBlue} 0%, ${colors.darkBlue} 100%)`
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* ABM Logo Animation */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex items-center justify-center gap-4 mb-8"
            >
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white font-bold text-4xl shadow-2xl"
                style={{ backgroundColor: colors.pinkMagenta, color: "blue" }}
              >
                A
              </div>
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center font-bold text-4xl shadow-2xl"
                style={{ backgroundColor: colors.freshGreen, color: "blue" }}
              >
                B
              </div>
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-white font-bold text-4xl shadow-2xl"
                style={{ backgroundColor: colors.goldenYellow, color: "blue" }}
              >
                M
              </div>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              ABM Group One
            </h1>
            <p className="text-lg italic mb-4" style={{ color: colors.goldenYellow }}>
              "Everyday Products, Everyday Earnings — Your Growth Today, Your Family's Security Tomorrow."
            </p>
            <p className="text-xl mb-6" style={{ color: colors.lightOrange }}>
              Pioneering the Future of Network Marketing
            </p>
            <p className="text-base text-white/85 max-w-3xl mx-auto">
              ABM Group One is an emerging, trusted, and modern FMCG-MLM hybrid platform in India that offers quality everyday-use products along with stable and respectable income opportunities.
              We are building a strong, transparent, and ethical business ecosystem where every member receives equal opportunity, proper training, and a platform for long-term growth and success.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== OUR STORY ===== */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-6" style={{ color: colors.nearBlack }}>
                Our Story
              </h2>
              <div className="space-y-4 text-base" style={{ color: colors.oliveBrown }}>
                <p>
                  ABM Group One was founded with the objective of bringing a positive and transparent transformation to the network marketing industry.
                  We identified the need for a platform that provides proper guidance to its members, offers a clear and easy-to-understand payout system, and strictly follows ethical standards and regulatory compliance.
                </p>
                <p>
                  Today, ABM Group One is rapidly growing with thousands of successful business partners moving forward together.
                  Our true identity is defined by the success of our members.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold" style={{ color: colors.deepOrange }}>50K+</div>
                  <div className="text-sm" style={{ color: colors.oliveBrown }}>Happy Members</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold" style={{ color: colors.deepOrange }}>₹100Cr+</div>
                  <div className="text-sm" style={{ color: colors.oliveBrown }}>Payouts Done</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold" style={{ color: colors.deepOrange }}>5+</div>
                  <div className="text-sm" style={{ color: colors.oliveBrown }}>Years of Trust</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl p-8">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                    <FaRocket className="text-4xl mx-auto mb-2" style={{ color: colors.brightBlue }} />
                    <p className="font-bold">2019</p>
                    <p className="text-xs">Founded</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                    <FaUsers className="text-4xl mx-auto mb-2" style={{ color: colors.freshGreen }} />
                    <p className="font-bold">50K+</p>
                    <p className="text-xs">Members</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                    <FaGlobe className="text-4xl mx-auto mb-2" style={{ color: colors.deepOrange }} />
                    <p className="font-bold">20+</p>
                    <p className="text-xs">States</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                    <FaAward className="text-4xl mx-auto mb-2" style={{ color: colors.goldenYellow }} />
                    <p className="font-bold">10+</p>
                    <p className="text-xs">Awards</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== MISSION & VISION ===== */}
      <section className="py-20" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl p-8 shadow-xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center text-3xl mb-6"
                  style={{ color: colors.brightBlue }}>
                  <FaBullseye />
                </div>
                <h3 className="text-3xl font-bold mb-4" style={{ color: colors.nearBlack }}>Our Mission</h3>
                <p className="text-sm italic mb-4" style={{ color: colors.brightBlue }}>
                  "To deliver high-quality FMCG products to every household while providing each member with a stable, secure, and dignified income opportunity."
                </p>
                <div className="mt-2 space-y-3">
                  {[
                    'Provide a secure and compliance-based business model',
                    'Ensure simple and transparent operations through modern digital platforms',
                    'Focus on training and leadership development',
                    'Create a long-term and sustainable income structure'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <FaCheckCircle style={{ color: colors.brightBlue }} />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-xl bg-orange-100 flex items-center justify-center text-3xl mb-6"
                  style={{ color: colors.deepOrange }}>
                  <FaEye />
                </div>
                <h3 className="text-3xl font-bold mb-4" style={{ color: colors.nearBlack }}>Our Vision</h3>
                <p className="text-sm italic mb-4" style={{ color: colors.deepOrange }}>
                  "To become India's most trusted FMCG-MLM hybrid platform, where everyday products empower every member financially and secure the future of their families."
                </p>
                <div className="mt-2 space-y-3">
                  {[
                    'Establish a transparent, trustworthy & technology-driven network model',
                    'Enable every individual to grow according to their potential',
                    'Achieve financial independence for all members',
                    'Pan-India presence and long-term sustainable growth'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <FaCheckCircle style={{ color: colors.deepOrange }} />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CORE VALUES ===== */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
              Our Core Values
            </h2>
            <p className="text-xl" style={{ color: colors.oliveBrown }}>
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-6 max-w-5xl mx-auto">
            {[
              { 
                icon: FaHandshake, 
                title: 'Trust', 
                desc: 'Building lasting relationships through reliability and honesty',
                color: colors.brightBlue 
              },
              { 
                icon: FaAward, 
                title: 'Quality', 
                desc: 'Every product undergoes strict quality checks before reaching customers',
                color: colors.deepOrange 
              },
              { 
                icon: FaShieldAlt, 
                title: 'Transparency', 
                desc: 'Complete clarity in all transactions and operations',
                color: colors.freshGreen 
              },
              { 
                icon: FaChartLine, 
                title: 'Growth', 
                desc: 'Continuous personal and financial development for every member',
                color: colors.goldenYellow 
              },
              { 
                icon: FaHeart, 
                title: 'Responsibility', 
                desc: 'Ethical conduct and compliance in every aspect of business',
                color: colors.pinkMagenta 
              }
            ].map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="bg-slate-50 p-6 rounded-xl text-center hover:shadow-lg transition hover:-translate-y-2"
              >
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl"
                  style={{ backgroundColor: value.color + '20', color: value.color }}>
                  <value.icon />
                </div>
                <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                <p className="text-sm" style={{ color: colors.oliveBrown }}>{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="py-20" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-6" style={{ color: colors.nearBlack }}>
                Why Choose ABM Group One?
              </h2>
              
              <div className="space-y-3">
                {[
                  {
                    title: '100% Legal and Transparent System',
                    desc: 'The company operates with full compliance and clarity in its structure.',
                    icon: FaShieldAlt,
                    color: colors.brightBlue
                  },
                  {
                    title: 'High-Quality FMCG Products',
                    desc: 'Every product undergoes strict quality checks before reaching customers.',
                    icon: FaAward,
                    color: colors.freshGreen
                  },
                  {
                    title: 'Ethical and Clean MLM Structure',
                    desc: 'No confusion, no hidden conditions — only a fair and honest earning model.',
                    icon: FaHandshake,
                    color: colors.tealBlue
                  },
                  {
                    title: 'High Repurchase Potential',
                    desc: 'Daily-use products ensure consistent purchasing, resulting in stable income flow.',
                    icon: FaCoins,
                    color: colors.deepOrange
                  },
                  {
                    title: 'Power-Based Automated Income System',
                    desc: 'An effective system designed to accelerate both business growth and income generation.',
                    icon: FaRocket,
                    color: colors.goldenYellow
                  },
                  {
                    title: 'Attractive Ranks, Rewards, Bonuses & Royalties',
                    desc: 'Multiple achievement levels with structured incentives and recognition programs.',
                    icon: FaTrophy,
                    color: colors.pinkMagenta
                  },
                  {
                    title: 'Long-Term and Sustainable Business Model',
                    desc: 'A system built to ensure stable, secure, and continuous growth for years.',
                    icon: FaChartLine,
                    color: colors.darkGreen
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0"
                      style={{ backgroundColor: item.color + '20', color: item.color }}>
                      <item.icon />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{item.title}</h3>
                      <p className="text-sm" style={{ color: colors.oliveBrown }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right Content - Stats */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-8 text-white"
            >
              <h3 className="text-2xl font-bold mb-6">Our Impact in Numbers</h3>
              
              <div className="space-y-6">
                <div className="flex justify-between items-center border-b border-white/20 pb-4">
                  <span>Happy Members</span>
                  <span className="text-3xl font-bold" style={{ color: colors.goldenYellow }}>50K+</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/20 pb-4">
                  <span>Payouts Distributed</span>
                  <span className="text-3xl font-bold" style={{ color: colors.goldenYellow }}>₹100Cr+</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/20 pb-4">
                  <span>Active Pickup Centers</span>
                  <span className="text-3xl font-bold" style={{ color: colors.goldenYellow }}>500+</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/20 pb-4">
                  <span>Mobile Stores</span>
                  <span className="text-3xl font-bold" style={{ color: colors.goldenYellow }}>2000+</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>States Covered</span>
                  <span className="text-3xl font-bold" style={{ color: colors.goldenYellow }}>20+</span>
                </div>
              </div>

              <div className="mt-8 p-4 bg-white/10 rounded-lg">
                <p className="text-sm text-center">Growing stronger every single day</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== BUSINESS OPPORTUNITY ===== */}
      <section className="py-20" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container mx-auto px-6 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl font-bold mb-3" style={{ color: colors.nearBlack }}>
              Business Opportunity
            </h2>
            <p className="text-base" style={{ color: colors.oliveBrown }}>
              This platform is specially designed for everyone who wants to grow
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { label: 'Young Entrepreneurs', icon: FaRocket, color: colors.brightBlue },
              { label: 'Homemakers', icon: FaHeart, color: colors.pinkMagenta },
              { label: 'Students', icon: FaStar, color: colors.goldenYellow },
              { label: 'Working Professionals', icon: FaUserTie, color: colors.deepOrange },
              { label: 'Retail Customers', icon: FaHandshake, color: colors.freshGreen },
              { label: 'Network Builders', icon: FaUsers, color: colors.tealBlue }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.07 }}
                className="flex items-center gap-4 bg-white rounded-xl shadow-sm px-5 py-4"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0"
                  style={{ backgroundColor: item.color + '20', color: item.color }}>
                  <item.icon />
                </div>
                <span className="font-semibold" style={{ color: colors.nearBlack }}>{item.label}</span>
                <FaCheckCircle className="ml-auto" style={{ color: item.color }} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center py-5 px-8 rounded-2xl"
            style={{ background: `linear-gradient(135deg, ${colors.navyBlue}, ${colors.darkBlue})` }}
          >
            <p className="text-white text-xl font-semibold italic">
              "The beginning is yours... and so is the success."
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== SLOGANS ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl font-bold mb-3" style={{ color: colors.nearBlack }}>
              Earn with Everyday FMCG Products
            </h2>
            <p className="text-base" style={{ color: colors.oliveBrown }}>Our Guiding Slogans</p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-4">
            {[
              { text: 'Daily Needs, Daily Benefits.', color: colors.brightBlue },
              { text: 'Products with Progress.', color: colors.freshGreen },
              { text: 'Buy Smart, Earn Daily.', color: colors.deepOrange },
              { text: 'Your Business, Your Income.', color: colors.pinkMagenta },
              { text: "Today's Necessity, Tomorrow's Security.", color: colors.goldenYellow }
            ].map((slogan, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                className="rounded-xl p-5 text-center shadow-md"
                style={{ background: slogan.color + '15', borderLeft: `4px solid ${slogan.color}` }}
              >
                <p className="font-bold text-sm" style={{ color: slogan.color }}>
                  {idx + 1}.
                </p>
                <p className="font-semibold text-sm mt-1 italic" style={{ color: colors.nearBlack }}>
                  "{slogan.text}"
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LEADERSHIP TEAM ===== */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
              Our Leadership
            </h2>
            <p className="text-xl" style={{ color: colors.oliveBrown }}>
              Visionary leaders building a trusted & ethical platform
            </p>
          </motion.div>

          {/* Founder Card - full width highlight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto mb-10 rounded-2xl shadow-lg overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${colors.navyBlue} 0%, ${colors.darkBlue} 100%)` }}
          >
            <div className="p-8 flex flex-col md:flex-row items-center gap-6">
              <div className="w-24 h-24 rounded-full flex items-center justify-center text-5xl text-white flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${colors.goldenYellow}, ${colors.lightOrange})` }}>
                <FaCrown />
              </div>
              <div className="text-white text-center md:text-left">
                <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: colors.goldenYellow }}>
                  Founder &amp; Chief Executive Officer
                </p>
                <h3 className="text-2xl font-bold mb-1">Mr. Santosh Bharatvarshi</h3>
                <p className="text-sm mb-3 opacity-80">Visionary Leader | Strategic Thinker | Industry Expert</p>
                <p className="text-sm mb-4 opacity-90">
                  As the Founder of ABM Group One, he laid the foundation of a trusted, transparent, and ethical network marketing platform.
                </p>
                <ul className="flex flex-wrap gap-2 justify-center md:justify-start mb-3">
                  {['10+ Years of Business Experience', 'Long-Term Growth Vision', 'Ethical & Compliance-Based Model'].map((pt, i) => (
                    <li key={i} className="flex items-center gap-1 text-xs px-3 py-1 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.12)', color: colors.lightCyan }}>
                      <FaCheckCircle className="flex-shrink-0" style={{ color: colors.goldenYellow }} />
                      {pt}
                    </li>
                  ))}
                </ul>
                <p className="text-xs font-semibold" style={{ color: colors.goldenYellow }}>
                  Leadership Philosophy: Growth with Integrity
                </p>
              </div>
            </div>
          </motion.div>

          {/* Other Directors / Heads */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                role: 'Director - Operations & Strategy',
                name: 'Ms. Ranjeeta Sonkar',
                points: ['Strategic Planning & Expansion', 'Network Development Management', 'Operational Excellence'],
                focus: 'Structured Expansion & Sustainable Systems',
                color: colors.freshGreen
              },
              {
                role: 'Director - Human Resources',
                name: 'Mr. Arun Gupta',
                points: ['Training & Skill Development', 'Leadership Grooming', 'Positive Work Culture'],
                focus: 'Empowering People to Lead',
                color: colors.brightBlue
              },
              {
                role: 'Head - Marketing & Branding',
                name: 'Ms. Ranjana Patel',
                points: ['Brand Strategy', 'Digital Promotions', 'Market Positioning'],
                focus: 'Building Strong Market Presence',
                color: colors.pinkMagenta
              },
              {
                role: 'Head - Finance & Compliance',
                name: 'Mr. Ashish Mishra',
                points: ['Transparent Payout System', 'Financial Planning & Audit', 'Legal & Regulatory Compliance'],
                focus: 'Financial Discipline & Regulatory Compliance',
                color: colors.deepOrange
              }
            ].map((leader, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-xl shadow-md p-5 flex flex-col border-t-4"
                style={{ borderColor: leader.color }}
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl text-white mb-4 mx-auto"
                  style={{ background: `linear-gradient(135deg, ${leader.color}, ${leader.color}bb)` }}>
                  <FaUserTie />
                </div>
                <p className="text-xs font-semibold text-center mb-1" style={{ color: leader.color }}>{leader.role}</p>
                <h3 className="text-base font-bold text-center mb-3" style={{ color: colors.nearBlack }}>{leader.name}</h3>
                <ul className="space-y-1 mb-4 flex-1">
                  {leader.points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ color: colors.oliveBrown }}>
                      <FaCheckCircle className="mt-0.5 flex-shrink-0" style={{ color: leader.color }} />
                      {pt}
                    </li>
                  ))}
                </ul>
                <p className="text-xs font-semibold text-center pt-3 border-t" style={{ color: leader.color, borderColor: `${leader.color}33` }}>
                  Focus: {leader.focus}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MILESTONES ===== */}
      <section className="py-20" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
              Our Journey
            </h2>
            <p className="text-xl" style={{ color: colors.oliveBrown }}>
              Key milestones in our growth story
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {[
              { year: '2019', event: 'ABM Group One Founded', desc: 'Started with a vision to revolutionize MLM' },
              { year: '2020', event: 'First 10,000 Members', desc: 'Reached milestone of 10K happy partners' },
              { year: '2021', event: 'Launched Pickup Center Network', desc: 'Expanded physical presence across states' },
              { year: '2022', event: '₹50Cr Payouts Achieved', desc: 'Crossed ₹50 crores in member payouts' },
              { year: '2023', event: 'Mobile Store Integration', desc: 'Launched delivery boy and POS features' },
              { year: '2024', event: '₹100Cr+ Payouts', desc: 'Continuing to grow and empower members' }
            ].map((milestone, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="flex items-center gap-6 mb-6"
              >
                <div className="w-24 text-right font-bold text-xl" style={{ color: colors.deepOrange }}>
                  {milestone.year}
                </div>
                <div className="w-4 h-4 rounded-full bg-gradient-to-r from-orange-400 to-orange-600"></div>
                <div className="flex-1 bg-white p-4 rounded-lg shadow-md">
                  <h3 className="font-bold text-lg">{milestone.event}</h3>
                  <p className="text-sm" style={{ color: colors.oliveBrown }}>{milestone.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
              What Our Leaders Say
            </h2>
            <p className="text-xl" style={{ color: colors.oliveBrown }}>
              Hear from our top achievers
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                name: 'Sunita Verma',
                rank: 'Crown Diamond',
                quote: 'ABM transformed my life. From a homemaker to a business leader earning ₹2L+ monthly.',
                color: colors.goldenYellow
              },
              {
                name: 'Rahul Mehta',
                rank: 'Royal Crown',
                quote: 'The most transparent MLM platform. Timely payouts and amazing support team.',
                color: colors.brightBlue
              },
              {
                name: 'Kavita Singh',
                rank: 'Diamond Leader',
                quote: 'The matrix plan is brilliant. My team of 5000+ members is growing every day.',
                color: colors.freshGreen
              }
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-slate-50 p-6 rounded-xl shadow-lg"
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} style={{ color: colors.goldenYellow }} />
                  ))}
                </div>
                <p className="text-sm mb-4 italic" style={{ color: colors.oliveBrown }}>
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br flex items-center justify-center text-white font-bold"
                    style={{ background: `linear-gradient(135deg, ${testimonial.color}, ${testimonial.color}dd)` }}>
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold">{testimonial.name}</p>
                    <p className="text-xs" style={{ color: testimonial.color }}>{testimonial.rank}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== OFFICE LOCATIONS ===== */}
      <section className="py-20" style={{ backgroundColor: '#f8fafc' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.nearBlack }}>
              Our Presence
            </h2>
            <p className="text-xl" style={{ color: colors.oliveBrown }}>
              Pan-India operations with regional offices
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { state: 'Uttar Pradesh', type: 'Head Office', address: 'Ramnagar, Meja, Prayagraj - 212305', icon: FaBuilding },
              { state: 'Uttar Pradesh', type: 'Registered Office', address: 'Ramnagar, Meja, Prayagraj - 212305', icon: FaBuilding },
              { state: 'UP', type: 'Working Office', address: 'Naini, Prayagraj - 211008', icon: FaBuilding }
            ].map((office, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white p-4 rounded-lg shadow-md text-center"
              >
                <office.icon className="text-3xl mx-auto mb-2" style={{ color: colors.deepOrange }} />
                <p className="text-xs font-semibold" style={{ color: colors.skyBlue }}>{office.state}</p>
                <h3 className="font-bold mt-1">{office.type}</h3>
                <p className="text-xs mt-1" style={{ color: colors.oliveBrown }}>{office.address}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== GUIDING PRINCIPLE ===== */}
      <section className="py-16" style={{ background: `linear-gradient(135deg, ${colors.navyBlue} 0%, ${colors.tealBlue} 100%)` }}>
        <div className="container mx-auto px-6 max-w-4xl text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-6" style={{ color: colors.goldenYellow }}>
              Our Guiding Principle
            </h2>
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              {['Integrity', 'Innovation', 'Inclusive Growth'].map((val, i) => (
                <div key={i} className="flex items-center gap-2 text-xl font-bold">
                  {i > 0 && <span style={{ color: colors.goldenYellow }}>|</span>}
                  <span>{val}</span>
                </div>
              ))}
            </div>
            <p className="text-base opacity-85 mb-4">We believe:</p>
            <p className="text-2xl font-semibold italic" style={{ color: colors.lightCyan }}>
              "True success is the one achieved together."
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== CONTACT INFO ===== */}
      <section className="py-12 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="flex items-center justify-center gap-3">
              <FaPhone className="text-2xl" style={{ color: colors.lightOrange }} />
              <div>
                <p className="text-sm opacity-80">Call Us</p>
                <p className="font-bold">8887355992</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <FaEnvelope className="text-2xl" style={{ color: colors.lightOrange }} />
              <div>
                <p className="text-sm opacity-80">Email Us</p>
                <p className="font-bold">support.abmgroupone@gmail.com</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <FaClock className="text-2xl" style={{ color: colors.lightOrange }} />
              <div>
                <p className="text-sm opacity-80">Working Hours</p>
                <p className="font-bold">10:00 AM - 6:00 PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-20 relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.navyBlue}, ${colors.darkBlue})`
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-white"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-white"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Be Part of Our Success Story
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: colors.lightOrange }}>
            Join thousands of successful partners and start your journey with ABM Group One
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/register"
              className="px-10 py-4 bg-white rounded-lg font-bold text-lg hover:shadow-2xl transition transform hover:-translate-y-1"
              style={{ color: colors.navyBlue }}
            >
              Join Now
            </Link>
            <Link
              to="/contact"
              className="px-10 py-4 border-2 border-white rounded-lg font-bold text-lg hover:bg-white/20 transition"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;